import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calculator, Bank, Factory, UsersThree, EnvelopeSimple, ArrowUpRight, WarningOctagon, NotePencil, BellRinging, Check, Checks, MegaphoneSimple, Brain, CurrencyEur, Trash, CaretDown, Lightbulb, Headset, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, api } from "../context/AuthContext";
import { AREA_URLS } from "../config/areaUrls";

const TILES = [
  { key: "contabilita", name: "Contabilità", url: AREA_URLS.contabilita, icon: Calculator, span: "md:col-span-2", desc: "Gestione contabile e bilanci", testId: "menu-tile-contabilita", color: "#38BDF8" },
  { key: "tesoreria", name: "Tesoreria", url: AREA_URLS.tesoreria, icon: Bank, span: "md:col-span-2", desc: "Flussi di cassa e pagamenti", testId: "menu-tile-tesoreria", color: "#E9A23B" },
  { key: "paghe", name: "Paghe", url: AREA_URLS.paghe, icon: CurrencyEur, span: "md:col-span-2", desc: "Dipendenti · Cedolini · Presenze", testId: "menu-tile-paghe", color: "#6366F1" },
  { key: "produzione", name: "Produzione", url: AREA_URLS.produzione, icon: Factory, span: "md:col-span-2", desc: "Cicli produttivi", testId: "menu-tile-produzione", color: "#9F1239" },
  { key: "posta", name: "Posta", url: AREA_URLS.posta, icon: EnvelopeSimple, span: "md:col-span-2", desc: "Posta aziendale", testId: "menu-tile-posta", color: "#F97316" },
  { key: "sohelp", name: "So.Help", url: AREA_URLS.sohelp, icon: Headset, span: "md:col-span-2", desc: "Helpdesk e assistenza", testId: "menu-tile-sohelp", color: "#10B981" },
  { key: "risorice", name: "Risorice", url: AREA_URLS.risorice, icon: UsersThree, span: "md:col-span-2", desc: "Gestione risorse", testId: "menu-tile-risorice", color: "#E05E3F" },
  { key: "paqway", name: "PAQ.WAY", url: AREA_URLS.paqway, icon: Storefront, span: "md:col-span-2", desc: "Vendite · Ordini · Provvigioni", testId: "menu-tile-paqway", color: "#F43F5E" },
  { key: "viki", name: "V.I.K.I.", url: "/viki", icon: Brain, span: "md:col-span-2", desc: "L'assistente AI dell'ecosistema PAQ", testId: "menu-tile-viki", color: "#45E0D0", internal: true, rolesOnly: ["master", "admin"] },
];

const ALERT_META = {
  seo: { label: "SEO", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  backup: { label: "Backup", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  ads: { label: "Ads", cls: "bg-sky-400/15 text-sky-300 border-sky-400/30" },
  report: { label: "Report", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  doctor: { label: "Doctor", cls: "bg-violet-400/15 text-violet-300 border-violet-400/30" },
  banca: { label: "Banca", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
};

const ALERT_SOLUTIONS = {
  seo: { text: "Apri Monitor → SITI e verifica lo stato del dominio segnalato: raggiungibilità, certificato e posizionamento. Se il problema persiste, chiedi a VIKI un'analisi del sito.", to: "/monitor/siti", label: "Vai a Monitor SITI" },
  backup: { text: "Vai in Manutenzione ed esegui subito un backup manuale. Se fallisce di nuovo, controlla lo spazio disco e la configurazione SMTP Comunicazioni.", to: "/manutenzione", label: "Vai a Manutenzione" },
  ads: { text: "Controlla la spesa e il tetto mensile nella sezione Google Ads del Marketing: valuta di mettere in pausa le campagne meno efficaci o alzare il tetto.", to: "/marketing/googleads", label: "Vai a Google Ads" },
  report: { text: "Consulta il report completo per il dettaglio dei numeri: se i dati sono in linea non serve alcuna azione.", to: "/analisi", label: "Vai ad Analisi" },
  doctor: { text: "Apri Monitor → APP per il dettaglio del collegamento in errore e riesegui la visita Doctor dopo l'intervento.", to: "/monitor/app", label: "Vai a Monitor APP" },
  banca: { text: "Il saldo complessivo dei conti collegati è sceso sotto la soglia impostata: controlla i movimenti in Tesoreria e valuta la soglia in Manutenzione → Gateway Fiscale.", to: "/manutenzione", label: "Vai a Manutenzione" },
};

export default function MenuPage() {
  const { user } = useAuth();
  const [backupAlert, setBackupAlert] = useState(null);
  const [siteNotes, setSiteNotes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [openNotice, setOpenNotice] = useState(null);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [monthSpend, setMonthSpend] = useState(null);
  const [tileStats, setTileStats] = useState({});
  const navigate = useNavigate();
  const visibleTiles = TILES.filter((t) => {
    if (t.rolesOnly) return t.rolesOnly.includes(user?.role);
    return !user?.tiles || user.tiles.includes(t.key);
  });

  const loadAlerts = (includeRead = showArchived) => {
    api.get(`/admin/alerts/recent?include_read=${includeRead}`).then(({ data }) => setAlerts(data)).catch(() => {});
  };

  useEffect(() => {
    api.post("/auth/refresh").catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "master") {
      api.get("/admin/maintenance/backup-alert").then(({ data }) => setBackupAlert(data)).catch(() => {});
      api.get("/admin/web/notes").then(({ data }) => setSiteNotes(data)).catch(() => {});
      api.get("/admin/ads/month-spend").then(({ data }) => setMonthSpend(data)).catch(() => {});
      api.get("/menu/tile-stats").then(({ data }) => setTileStats(data)).catch(() => {});
      loadAlerts(false);
    }
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const archiveAlert = async (a) => {
    try {
      await api.post(`/admin/alerts/${a.id}/read`);
      if (showArchived) {
        setAlerts((list) => list.map((x) => (x.id === a.id ? { ...x, read: true } : x)));
      } else {
        setAlerts((list) => list.filter((x) => x.id !== a.id));
      }
    } catch (err) {
      toast.error("Archiviazione non riuscita");
    }
  };

  const deleteAlert = async (a) => {
    try {
      await api.delete(`/admin/alerts/${a.id}`);
      setAlerts((list) => list.filter((x) => x.id !== a.id));
      toast.success("Avviso eliminato");
    } catch (err) {
      toast.error("Eliminazione non riuscita");
    }
  };

  const archiveAll = async () => {
    try {
      const { data } = await api.post("/admin/alerts/read-all");
      toast.success(data.message);
      loadAlerts();
    } catch (err) {
      toast.error("Archiviazione non riuscita");
    }
  };

  const toggleArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    loadAlerts(next);
  };

  const openTile = async (tile) => {
    if (tile.internal) {
      navigate(tile.url);
      return;
    }
    try {
      await Promise.race([api.post("/auth/refresh"), new Promise((r) => setTimeout(r, 1500))]);
    } catch (e) { /* redirect comunque */ }
    window.location.href = tile.url;
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="menu-page">
        {user?.role === "master" && (backupAlert?.alert || (monthSpend?.available && monthSpend.cap)) && (
          <div className="mb-6 flex items-center gap-3 animate-fade-up" data-testid="dashboard-notice-icons">
            {backupAlert?.alert && (
              <button type="button" data-testid="notice-icon-backup" title="Backup: attenzione richiesta — clicca per il dettaglio"
                onClick={() => setOpenNotice(openNotice === "backup" ? null : "backup")}
                className={`relative h-11 px-4 rounded-full border inline-flex items-center justify-center gap-2 transition-colors ${openNotice === "backup" ? "bg-red-500/20 border-red-500/60" : "bg-white/5 border-red-500/30 hover:bg-red-500/10"}`}>
                <WarningOctagon size={22} weight="duotone" className="text-red-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-red-400">Alert!</span>
                <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse border border-black/60" />
              </button>
            )}
            {monthSpend?.available && monthSpend.cap && (
              <button type="button" data-testid="notice-icon-ads" title={`Spesa Google Ads: ${monthSpend.pct.toFixed(0)}% del tetto mensile — clicca per il dettaglio`}
                onClick={() => setOpenNotice(openNotice === "ads" ? null : "ads")}
                className={`relative w-11 h-11 rounded-full border inline-flex items-center justify-center transition-colors ${openNotice === "ads" ? "bg-amber-400/20 border-amber-400/60" : "bg-white/5 border-amber-400/30 hover:bg-amber-400/10"}`}>
                <MegaphoneSimple size={22} weight="duotone" className="text-amber-400" />
                <span className={`absolute -top-1 -right-1 min-w-[22px] h-[15px] px-1 rounded-full text-[9px] font-bold inline-flex items-center justify-center leading-none border border-black/60 ${monthSpend.pct >= 100 ? "bg-red-500 text-white" : monthSpend.pct >= 80 ? "bg-amber-400 text-black" : "bg-emerald-500 text-white"}`}>
                  {Math.min(monthSpend.pct, 999).toFixed(0)}%
                </span>
              </button>
            )}
          </div>
        )}
        {user?.role === "master" && openNotice === "backup" && backupAlert?.alert && (
          <div className="mb-8 p-4 rounded-xl border border-red-500/30 bg-red-950/40 backdrop-blur-md shadow-[0_0_20px_rgba(239,68,68,0.1)] flex flex-wrap items-center gap-3 animate-fade-up" data-testid="backup-alert-banner">
            <span className="relative flex items-center">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse mr-1" />
              <WarningOctagon size={22} weight="duotone" className="text-red-400 shrink-0" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-400">{backupAlert.message}</p>
              <p className="text-xs text-white/50 mt-0.5">
                {backupAlert.error ? `Errore: ${backupAlert.error} — ` : ""}
                Ultimo tentativo: {backupAlert.last_attempt ? new Date(backupAlert.last_attempt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}
              </p>
            </div>
            <Link to="/manutenzione" data-testid="backup-alert-link"
              className="h-9 px-4 inline-flex items-center rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors">
              Vai a Manutenzione
            </Link>
          </div>
        )}
        {user?.role === "master" && openNotice === "ads" && monthSpend?.available && monthSpend.cap && (
          <div className="mb-8 p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md hover:border-white/20 transition-colors animate-fade-up" data-testid="ads-month-widget">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <MegaphoneSimple size={16} weight="duotone" className="text-amber-400" />
              <p className="text-xs font-semibold text-white/80">
                Spesa Google Ads — {new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
              </p>
              <p className="ml-auto text-xs">
                <span className="font-heading text-base font-bold text-white">€ {monthSpend.spend.toFixed(2)}</span>
                <span className="text-white/40"> di € {monthSpend.cap.toFixed(2)}</span>
                <span data-testid="ads-month-pct" className={`ml-2 font-semibold ${monthSpend.pct >= 100 ? "text-red-400" : monthSpend.pct >= 80 ? "text-amber-300" : "text-emerald-400"}`}>
                  {monthSpend.pct.toFixed(0)}%
                </span>
              </p>
            </div>
            <div className="h-2 rounded-full bg-black/50 overflow-hidden" data-testid="ads-month-bar">
              <div className={`h-full rounded-full transition-[width] duration-700 ${monthSpend.pct >= 100 ? "bg-gradient-to-r from-red-600 to-red-400 shadow-[0_0_10px_rgba(248,113,113,0.5)]" : monthSpend.pct >= 80 ? "bg-gradient-to-r from-amber-500 to-amber-300 shadow-[0_0_10px_rgba(251,191,36,0.5)]" : "bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.5)]"}`}
                style={{ width: `${Math.min(monthSpend.pct, 100)}%` }} />
            </div>
          </div>
        )}
        <div className="mb-12 animate-fade-up">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-2" style={{ color: "rgba(69,224,208,0.85)" }}>Bentornato</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">
            Ciao, {(user?.name || user?.email?.split("@")[0] || "").split(" ")[0]}
          </h1>
          <p className="text-lg text-white/60 font-light mt-3">Seleziona un'area per accedere allo strumento dedicato.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:auto-rows-[200px]">
          {visibleTiles.map((tile, i) => {
            const Icon = tile.icon;
            const isViki = tile.key === "viki";
            return (
              <button
                key={tile.name}
                data-testid={tile.testId}
                onClick={() => openTile(tile)}
                style={{
                  animationDelay: `${i * 80}ms`,
                  "--tile-color": tile.color,
                }}
                className={`${tile.span} animate-fade-up group relative text-left h-full min-h-[180px] flex flex-col justify-between p-6 rounded-2xl bg-white/[0.03] backdrop-blur-md border ${isViki ? "border-[#45E0D0]/35 hover:shadow-[0_0_44px_rgba(69,224,208,0.22)]" : "border-white/10 hover:shadow-[0_14px_44px_-14px_var(--tile-color)]"} transition-[transform,border-color,background-color,box-shadow] duration-300 ease-out hover:-translate-y-1 hover:border-[var(--tile-color)] focus:outline-none focus:ring-2 focus:ring-white/40`}
              >
                <span className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                  <span className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: tile.color }} />
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `radial-gradient(60% 90% at 85% 0%, ${tile.color}14 0%, transparent 70%)` }} />
                  {isViki && (
                    <span className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(69,224,208,0.10) 0%, transparent 45%, rgba(69,224,208,0.06) 100%)" }} />
                  )}
                </span>
                {isViki && (
                  <img src="/viki/viki-idle.png" alt="V.I.K.I." draggable={false}
                    className="absolute left-6 top-1/2 -translate-y-1/2 w-20 h-20 md:w-24 md:h-24 z-10 viki-floaty transition-transform duration-500 ease-out group-hover:scale-110"
                    style={{ objectFit: "contain", filter: "drop-shadow(0 8px 16px rgba(69,224,208,0.25))" }} />
                )}
                <div className={`relative z-10 flex items-start justify-between ${isViki ? "pl-28 md:pl-32" : ""}`}>
                  {isViki ? (
                    <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: "rgba(69,224,208,0.9)" }}>
                      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#45E0D0", boxShadow: "0 0 8px #45E0D0" }} />
                      Assistente AI attiva
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center w-16 h-16 rounded-xl transition-colors duration-300" style={{ backgroundColor: `${tile.color}1A` }}>
                      <Icon size={40} weight="duotone" style={{ color: tile.color }} />
                    </span>
                  )}
                  <ArrowUpRight size={20} className="text-white/20 transition-colors duration-300 group-hover:text-white" />
                </div>
                <div className={`relative z-10 ${isViki ? "pl-28 md:pl-32" : ""}`}>
                  <h3 className={`font-heading font-semibold tracking-tight ${isViki ? "text-2xl" : "text-xl"}`}
                    style={isViki ? { color: "#45E0D0", textShadow: "0 0 10px rgba(69,224,208,0.45)" } : undefined}>{tile.name}</h3>
                  <p className="text-xs text-white/55 mt-1">{tile.desc}</p>
                  {!isViki && tileStats[tile.key] && (tileStats[tile.key].kpi?.length > 0 || tileStats[tile.key].stato) && (
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 mt-2" data-testid={`tile-stats-${tile.key}`}>
                      {tileStats[tile.key].stato && (
                        <span title={`Collegamento: ${tileStats[tile.key].stato}`}
                          className={`w-1.5 h-1.5 rounded-full self-center ${tileStats[tile.key].stato === "ok" ? "bg-emerald-400" : "bg-amber-400 animate-pulse"}`} />
                      )}
                      {(tileStats[tile.key].kpi || []).map((k) => (
                        <span key={k.label} className="inline-flex items-baseline gap-1.5 min-w-0">
                          <span className="font-heading text-sm font-bold" style={{ color: tile.color }}>
                            {k.value}{k.unit ? ` ${k.unit}` : ""}
                          </span>
                          <span className="text-[9px] uppercase tracking-wider text-white/40 truncate">{k.label}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {user?.role === "master" && siteNotes.length > 0 && (
          <div className="mt-10 animate-fade-up" data-testid="dashboard-site-notes">
            <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-1.5">
              <NotePencil size={13} className="text-sky-400" /> Promemoria siti
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {siteNotes.map((n) => (
                <Link key={n.domain} to="/monitor/siti" data-testid={`dashboard-note-${n.domain}`}
                  className="p-4 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 border-l-4 border-l-sky-400 hover:bg-white/[0.08] hover:border-white/20 transition-colors block">
                  <p className="text-sm font-semibold text-white flex items-center justify-between">
                    {n.domain}
                    <span className="text-[10px] font-normal text-white/35">{n.updated_at ? new Date(n.updated_at).toLocaleDateString("it-IT") : ""}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{n.note}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {user?.role === "master" && (alerts.length > 0 || showArchived) && (
          <div className="mt-10 animate-fade-up" data-testid="dashboard-alerts-history">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <p className="text-xs tracking-[0.3em] uppercase text-white/40 flex items-center gap-1.5">
                <BellRinging size={13} className="text-amber-400" /> {showArchived ? "Storico avvisi (tutti)" : "Avvisi da controllare"}
              </p>
              <div className="ml-auto flex gap-2">
                {!showArchived && alerts.length > 0 && (
                  <button type="button" data-testid="dashboard-alerts-archive-all" onClick={archiveAll}
                    className="h-7 px-3 rounded-full border border-white/15 text-[10px] font-semibold text-white/50 hover:text-white hover:bg-white/5 transition-colors inline-flex items-center gap-1">
                    <Checks size={12} /> Archivia tutti
                  </button>
                )}
                <button type="button" data-testid="dashboard-alerts-show-archived" onClick={toggleArchived}
                  className={`h-7 px-3 rounded-full border text-[10px] font-semibold transition-colors ${showArchived ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                  {showArchived ? "Solo da controllare" : "Mostra archiviati"}
                </button>
              </div>
            </div>
            <div className="border border-white/10 rounded-2xl bg-white/5 backdrop-blur-md divide-y divide-white/5 overflow-hidden">
              {alerts.length === 0 && (
                <p className="px-4 py-5 text-xs text-white/40 text-center" data-testid="dashboard-alerts-empty">Nessun avviso.</p>
              )}
              {(showAllAlerts ? alerts : alerts.slice(0, 5)).map((a, i) => {
                const meta = ALERT_META[a.category] || ALERT_META.seo;
                const sol = ALERT_SOLUTIONS[a.category] || ALERT_SOLUTIONS.seo;
                const isOpen = expandedAlert === a.id;
                return (
                  <div key={a.id} data-testid={`dashboard-alert-${i}`} className={`${a.read ? "opacity-60" : ""}`}>
                    <div className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors">
                      <span className={`shrink-0 mt-0.5 inline-flex items-center text-[9px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <button type="button" onClick={() => setExpandedAlert(isOpen ? null : a.id)}
                        data-testid={`dashboard-alert-toggle-${i}`} className="min-w-0 flex-1 text-left">
                        <p className="text-sm font-semibold text-white/85 truncate">{a.title.replace("AREAPAQ — ", "")}</p>
                        {!isOpen && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{a.body}</p>}
                      </button>
                      <span className="shrink-0 text-[10px] text-white/35 mt-1 hidden sm:block">
                        {new Date(a.timestamp).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                      {!a.read && (
                        <button type="button" data-testid={`dashboard-alert-read-${i}`} onClick={() => archiveAlert(a)}
                          title="Segna come letto (archivia)"
                          className="shrink-0 w-7 h-7 rounded-full border border-white/15 text-white/40 hover:text-emerald-300 hover:border-emerald-400/40 hover:bg-emerald-400/10 transition-colors inline-flex items-center justify-center">
                          <Check size={13} weight="bold" />
                        </button>
                      )}
                      <button type="button" data-testid={`dashboard-alert-delete-${i}`} onClick={() => deleteAlert(a)}
                        title="Elimina definitivamente l'avviso"
                        className="shrink-0 w-7 h-7 rounded-full border border-white/15 text-white/40 hover:text-red-400 hover:border-red-500/40 hover:bg-red-500/10 transition-colors inline-flex items-center justify-center">
                        <Trash size={13} />
                      </button>
                      <button type="button" onClick={() => setExpandedAlert(isOpen ? null : a.id)}
                        className="shrink-0 w-7 h-7 rounded-full text-white/40 hover:text-white transition-colors inline-flex items-center justify-center">
                        <CaretDown size={13} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 pl-14" data-testid={`dashboard-alert-detail-${i}`}>
                        <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{a.body || "Nessun dettaglio aggiuntivo."}</p>
                        <div className="mt-3 p-3 rounded-xl bg-emerald-400/[0.06] border border-emerald-400/20">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5 mb-1">
                            <Lightbulb size={12} weight="duotone" /> Possibile soluzione
                          </p>
                          <p className="text-xs text-slate-300 leading-relaxed">{sol.text}</p>
                          <Link to={a.url || sol.to} data-testid={`dashboard-alert-solution-link-${i}`}
                            className="mt-2 inline-flex items-center gap-1 h-7 px-3 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[11px] font-semibold hover:bg-emerald-500/25 transition-colors">
                            {sol.label} <ArrowUpRight size={11} />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {alerts.length > 5 && (
                <button type="button" data-testid="dashboard-alerts-toggle" onClick={() => setShowAllAlerts((s) => !s)}
                  className="w-full py-2.5 text-xs font-semibold text-white/40 hover:text-white transition-colors">
                  {showAllAlerts ? "Mostra meno" : `Mostra tutti (${alerts.length})`}
                </button>
              )}
            </div>
          </div>
        )}
      </main>
  );
}
